import type { SaveableCardStatus } from "@jasper/forms";
import { notifications } from "@mantine/notifications";
import { useStore } from "@tanstack/react-form";
import { useEffect, useRef, useState } from "react";

const SAVED_DURATION_MS = 1500;

// Structural slice of a TanStack form: enough to read its values reactively
type PreferenceFormLike<TValues> = {
  store: {
    get: () => { values: TValues };
    subscribe: (listener: (state: { values: TValues }) => void) => {
      unsubscribe: () => void;
    };
  };
};

const pickFields = <TValues, TField extends keyof TValues>(
  values: TValues,
  fields: readonly TField[]
) =>
  Object.fromEntries(fields.map(field => [field, values[field]])) as Pick<
    TValues,
    TField
  >;

const isSameValue = (a: unknown, b: unknown) =>
  Array.isArray(a) && Array.isArray(b)
    ? a.length === b.length && a.every((item, index) => item === b[index])
    : a === b;

// One card = a subset of the tab's form fields, saved on demand. The card is
// dirty while its fields differ from the last saved snapshot.
export const usePreferenceCardSave = <TValues, TField extends keyof TValues>(
  form: PreferenceFormLike<TValues>,
  fields: readonly TField[],
  onSave: (values: Pick<TValues, TField>) => Promise<unknown> = () =>
    Promise.resolve()
) => {
  const values = useStore(form.store, state => state.values);
  const [savedValues, setSavedValues] = useState(() =>
    pickFields(values, fields)
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const isDirty = fields.some(
    field => !isSameValue(values[field], savedValues[field])
  );

  const save = async () => {
    const valuesToSave = pickFields(values, fields);
    setSaving(true);
    try {
      await onSave(valuesToSave);
      setSavedValues(valuesToSave);
      clearTimeout(timeoutRef.current);
      setSaved(true);
      timeoutRef.current = setTimeout(() => setSaved(false), SAVED_DURATION_MS);
    } catch (error) {
      console.error("Failed to save preferences", error);
      notifications.show({
        title: "Préférences",
        message:
          "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const status: SaveableCardStatus = saving
    ? "saving"
    : isDirty
      ? "dirty"
      : saved
        ? "saved"
        : "idle";

  return { status, save };
};
